import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useToast } from '../../../hooks/use-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  Send,
  RefreshCw,
  Loader2,
  MessageSquare,
  Bot,
  User,
  CheckCircle,
  AlertCircle,
  Zap,
  Globe,
  Volume2,
  Mic,
  PlayCircle,
  X,
  Building2
} from 'lucide-react';

const AIChatSimulatorEnhanced = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { makeAuthenticatedRequest, getBackendUrl } = useCRMAuth();
  const { currentBusiness } = useBusiness();
  const location = useLocation();
  const messagesEndRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [chatActive, setChatActive] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [customerMessage, setCustomerMessage] = useState('');
  const [selectedScenario, setSelectedScenario] = useState(null);

  const stateData = location.state || {};

  const [chatForm, setChatForm] = useState({
    customer_phone: stateData.phoneNumber || '+1234567890',
    customer_name: user?.name || 'Test Customer',
    business_id: stateData.businessId || '',
    business_name: user?.business_name || 'Test Business Corp',
    test_email_override: ''
  });

  useEffect(() => {
    const brandId = currentBusiness?.brandId;
    if (brandId) {
      setChatForm(prev => ({ ...prev, business_id: prev.business_id || brandId }));
    }
  }, [currentBusiness?.brandId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  useEffect(() => {
    if (stateData.autoStart && !chatActive && !loading) {
      handleInitiateChat();
    }
  }, [stateData.autoStart]);

  const testScenarios = {
    basic: [
      { id: 'basic_1', name: 'Simple Technical Support', language: 'English', query: "Hi, I can't log into my account. Can you help me reset my password?", complexity: 'Low', expectedOutcome: 'Auto-resolved with password reset steps' },
      { id: 'basic_2', name: 'Billing Question', language: 'English', query: "What's the charge of $29.99 on my credit card from last month?", complexity: 'Low', expectedOutcome: 'Auto-resolved with billing explanation' },
      { id: 'basic_3', name: 'General Inquiry', language: 'English', query: "What are your business hours? When are you open?", complexity: 'Low', expectedOutcome: 'Auto-resolved with business hours' },
      { id: 'basic_4', name: 'Product Information', language: 'English', query: "Tell me about your premium subscription plan. What features does it include?", complexity: 'Low', expectedOutcome: 'Auto-resolved with product details' }
    ],
    complex: [
      { id: 'complex_1', name: 'Angry Customer - Service Complaint', language: 'English', query: "I'VE BEEN WAITING FOR 3 WEEKS AND STILL NO RESPONSE! This is absolutely UNACCEPTABLE! I want to speak to your MANAGER RIGHT NOW!", complexity: 'High', expectedOutcome: 'Escalated to Manager due to angry tone', emotion: 'Angry' },
      { id: 'complex_2', name: 'Frustrated Customer - Multiple Issues', language: 'English', query: "Look, I've tried everything you said last time, it STILL doesn't work. First the app crashes, then I can't access my data, and now your support is telling me different things. I'm beyond frustrated!", complexity: 'High', expectedOutcome: 'Escalated to Operator - multiple unresolved issues', emotion: 'Frustrated' },
      { id: 'complex_3', name: 'Anxious Customer - Urgent Account Issue', language: 'English', query: "Oh no, oh no... someone accessed my account! I'm seeing transactions I didn't make. Please help me quickly, I'm really worried!", complexity: 'High', expectedOutcome: 'Escalated immediately - security concern', emotion: 'Anxious' },
      { id: 'complex_4', name: 'Confused Customer - Technical Jargon', language: 'English', query: "Um... so I got this error message that says something about... SSL certificate? And then it mentioned HTTPS? I don't really understand what any of that means...", complexity: 'Medium', expectedOutcome: 'AI simplifies technical explanation', emotion: 'Confused' }
    ],
    ambiguous: [
      { id: 'ambiguous_1', name: 'Vague Problem Description', language: 'English', query: "It's not working. Can you fix it?", complexity: 'Medium', expectedOutcome: 'AI asks clarifying questions' },
      { id: 'ambiguous_2', name: 'Multiple Unclear Issues', language: 'English', query: "Something's wrong with my thing, and also that other thing isn't doing what it should do, you know?", complexity: 'Medium', expectedOutcome: 'AI seeks specific details' },
      { id: 'ambiguous_3', name: 'Incomplete Information', language: 'English', query: "Can you check my account... I think there was a problem last week... or was it two weeks ago?", complexity: 'Medium', expectedOutcome: 'AI requests account details' },
      { id: 'ambiguous_4', name: 'Nonsensical Query', language: 'English', query: "The purple elephant flew yesterday when my subscription was tomorrow, please help with the blue question mark?", complexity: 'High', expectedOutcome: 'AI recognizes confusion, escalates or seeks clarification' }
    ],
    multilingual: [
      { id: 'multilingual_1', name: 'French - Simple Support', language: 'French', query: "Bonjour, j'ai besoin d'aide avec mon compte. Je ne peux pas me connecter.", complexity: 'Low', expectedOutcome: 'AI responds in French with login help' },
      { id: 'multilingual_2', name: 'French - Billing Complaint', language: 'French', query: "Je ne comprends pas pourquoi vous m'avez facturé deux fois ce mois-ci! C'est inacceptable!", complexity: 'Medium', expectedOutcome: 'AI addresses billing issue in French', emotion: 'Upset' },
      { id: 'multilingual_3', name: 'French - Technical Problem', language: 'French', query: "L'application se bloque constamment et je perds toutes mes données. Pouvez-vous résoudre ce problème urgent?", complexity: 'Medium', expectedOutcome: 'AI troubleshoots in French' },
      { id: 'multilingual_4', name: 'French - Complex Escalation', language: 'French', query: "Cela fait trois semaines que j'attends une réponse! Je veux parler à votre responsable immédiatement! Cette situation est inadmissible!", complexity: 'High', expectedOutcome: 'Escalated to Manager - maintains French', emotion: 'Angry' },
      { id: 'multilingual_5', name: 'Code-Switching (Mixed Languages)', language: 'Mixed', query: "Hello, je voudrais savoir if I can change my subscription plan? C'est possible?", complexity: 'Medium', expectedOutcome: 'AI detects primary language and responds appropriately' }
    ],
    contextual: [
      { id: 'contextual_1', name: 'Follow-up Question', language: 'English', query: "Actually, one more thing about that...", complexity: 'Medium', expectedOutcome: 'AI maintains context from previous turn' },
      { id: 'contextual_2', name: 'Referencing Previous Issue', language: 'English', query: "Remember the problem I mentioned earlier? It's happening again.", complexity: 'Medium', expectedOutcome: 'AI recalls previous context' },
      { id: 'contextual_3', name: 'Changing Topic Mid-Conversation', language: 'English', query: "Wait, forget about that. I have a different question about billing instead.", complexity: 'Medium', expectedOutcome: 'AI adapts to topic change' },
      { id: 'contextual_4', name: 'Multiple Related Questions', language: 'English', query: "Can I upgrade my plan, and also, how much would that cost, and when would the charge happen?", complexity: 'Medium', expectedOutcome: 'AI addresses all questions systematically' }
    ],
    empathy: [
      { id: 'empathy_1', name: 'Disappointed Customer', language: 'English', query: "I've been a loyal customer for 5 years, and this is how you treat me? I'm really disappointed...", complexity: 'Medium', expectedOutcome: 'AI shows empathy, values loyalty', emotion: 'Disappointed' },
      { id: 'empathy_2', name: 'Stressed Customer', language: 'English', query: "I'm really stressed about this. I need it fixed urgently because I have a presentation tomorrow.", complexity: 'Medium', expectedOutcome: 'AI acknowledges stress, provides quick solution', emotion: 'Stressed' },
      { id: 'empathy_3', name: 'Grateful Customer', language: 'English', query: "Thank you so much for your help last time! It really saved me. Now I have another small question...", complexity: 'Low', expectedOutcome: 'AI acknowledges appreciation, maintains positive tone', emotion: 'Grateful' },
      { id: 'empathy_4', name: 'Patient Request', language: 'English', query: "I'm sorry, I'm not very good with technology. Could you explain this step-by-step? I might need you to repeat things...", complexity: 'Medium', expectedOutcome: 'AI adapts pace, provides clear instructions', emotion: 'Uncertain' }
    ]
  };

  const handleInitiateChat = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getBackendUrl()}/api/v1/ai-voice/simulate-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_phone: chatForm.customer_phone, business_id: chatForm.business_id })
      });

      if (!response.ok) throw new Error('Failed to initiate chat');

      const data = await response.json();

      setCurrentSession(data.session);
      setConversationHistory(data.session.conversation_history || []);
      setChatActive(true);

      toast({
        title: t('od.chatInitiated'),
        description: t('od.aiChatConnected', { session: data.session.session_id?.substring(0, 8) }),
      });
    } catch (error) {
      console.error('Error initiating chat:', error);
      toast({
        title: t('od.chatFailed'),
        description: t('od.chatFailedDesc'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (messageText) => {
    const textToSend = typeof messageText === 'string' ? messageText : customerMessage.trim();
    if (!textToSend || !currentSession) return;

    if (typeof messageText !== 'string') {
      setCustomerMessage('');
    }
    setSelectedScenario(null);
    setLoading(true);

    try {
      const response = await makeAuthenticatedRequest('/api/v1/ai-voice/process-conversation', {
        method: 'POST',
        body: JSON.stringify({
          session_id: currentSession.session_id,
          customer_text: textToSend,
          ...(chatForm.test_email_override ? { test_email_override: chatForm.test_email_override } : {})
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to process message: ${errorText}`);
      }

      const data = await response.json();

      setConversationHistory(prev => [
        ...prev,
        {
          speaker: 'customer',
          text: textToSend,
          timestamp: new Date().toISOString()
        },
        {
          speaker: 'ai',
          text: data.ai_response,
          timestamp: new Date().toISOString(),
          metadata: {
            detected_language: data.detected_language,
            detected_intent: data.detected_intent,
            confidence: data.confidence,
            should_escalate: data.should_escalate
          }
        }
      ]);
      setCurrentSession(prev => ({
        ...prev,
        session_id: data.session_id || prev.session_id,
        detected_language: data.detected_language,
        detected_intent: data.detected_intent,
        intent_confidence: data.intent_confidence
      }));

      if (data.should_escalate) {
        toast({
          title: t('od.chatEscalated'),
          description: t('od.escalatingTo', { target: data.escalate_to, reason: data.escalation_reason }),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error processing message:', error);
      toast({
        title: t('od.processingFailed'),
        description: error.message || t('od.processFailedFallback'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEndChat = () => {
    setChatActive(false);
    setCurrentSession(null);
    setConversationHistory([]);
    setCustomerMessage('');
    setSelectedScenario(null);
    toast({
      title: t('od.chatEnded'),
      description: t('od.aiChatTerminated'),
    });
  };

  const loadScenario = (scenario) => {
    setSelectedScenario(scenario);
    setCustomerMessage(scenario.query);
  };

  const handleQuickTest = async (scenario) => {
    if (!chatActive) {
      await handleInitiateChat();
      setTimeout(() => {
        loadScenario(scenario);
      }, 1000);
    } else {
      loadScenario(scenario);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <MessageSquare className="w-8 h-8 text-blue-600" />
              {t('nav.aiChatSimulator')}
              <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800 border-amber-300 text-sm font-semibold">
                {t('od.qaSimulatorMode')}
              </Badge>
            </h1>
            <p className="text-gray-600 mt-1">
              {t('od.chatSimSubtitle')}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="bg-white">
              <MessageSquare className="h-3 w-3 mr-1" />
              {t('od.userLabel')} {user?.name || user?.email || t('od.guest')}
            </Badge>
            <Badge variant="outline" className="text-xs" data-testid="crm-auth-status">
              {makeAuthenticatedRequest ? <CheckCircle className="w-3 h-3 mr-1 text-green-500" /> : <AlertCircle className="w-3 h-3 mr-1 text-red-500" />}
              {t('od.crmAuth')}
            </Badge>
            <Button size="sm" variant="outline" onClick={() => handleInitiateChat()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('od.restart')}
            </Button>
            <Badge variant="outline" className="bg-green-50 text-green-700">
              <CheckCircle className="h-3 w-3 mr-1" />
              {t('od.connected')}
            </Badge>
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
              <AlertCircle className="h-3 w-3 mr-1" />
              {t('od.aiActive')}
            </Badge>
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              <Zap className="h-3 w-3 mr-1" />
              {t('od.realTime')}
            </Badge>
            <Badge variant="outline" className="bg-purple-50 text-purple-700">
              <Volume2 className="h-3 w-3 mr-1" />
              {t('od.voiceReady')}
            </Badge>
            <Badge variant="outline" className="bg-pink-50 text-pink-700">
              <Mic className="h-3 w-3 mr-1" />
              {t('od.listeningShort')}
            </Badge>
          </div>
        </div>
      </div>

      {stateData.businessName && (
        <Card className="border-blue-500 bg-blue-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">
                {t('od.chattingWith')} <strong>{stateData.businessName}</strong>
              </span>
              <Badge variant="secondary" className="ml-auto">{stateData.phoneNumber}</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('od.testScenarios')}</CardTitle>
              <CardDescription>{t('od.selectScenarioChat')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="basic">
                <TabsList className="grid grid-cols-2 gap-1">
                  <TabsTrigger value="basic">{t('od.tabBasic')}</TabsTrigger>
                  <TabsTrigger value="complex">{t('od.tabComplex')}</TabsTrigger>
                </TabsList>
                <TabsList className="grid grid-cols-2 gap-1 mt-2">
                  <TabsTrigger value="ambiguous">{t('od.tabAmbiguous')}</TabsTrigger>
                  <TabsTrigger value="multilingual">{t('od.tabMultilingual')}</TabsTrigger>
                </TabsList>
                <TabsList className="grid grid-cols-2 gap-1 mt-2">
                  <TabsTrigger value="contextual">{t('od.tabContextual')}</TabsTrigger>
                  <TabsTrigger value="empathy">{t('od.tabEmpathy')}</TabsTrigger>
                </TabsList>

                {Object.entries(testScenarios).map(([key, scenarios]) => (
                  <TabsContent key={key} value={key} className="space-y-2 mt-4 max-h-96 overflow-y-auto">
                    {scenarios.map((scenario) => (
                      <Button
                        key={scenario.id}
                        variant="outline"
                        className="w-full justify-start text-left h-auto py-2"
                        onClick={() => handleQuickTest(scenario)}
                      >
                        <div className="flex-1">
                          <div className="font-medium text-sm">{scenario.name}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            <Badge variant="secondary" className="text-xs">
                              {key === 'multilingual' && <Globe className="w-3 h-3 mr-1" />}
                              {scenario.language || scenario.complexity || scenario.emotion}
                            </Badge>
                            {scenario.emotion && key !== 'empathy' && (
                              <Badge className="text-xs ml-1 bg-orange-500">{scenario.emotion}</Badge>
                            )}
                          </div>
                        </div>
                        <PlayCircle className="w-4 h-4" />
                      </Button>
                    ))}
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('od.chatControls')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('od.customerName')}</Label>
                  <Input
                    value={chatForm.customer_name}
                    onChange={(e) => setChatForm({ ...chatForm, customer_name: e.target.value })}
                    disabled={chatActive}
                  />
                </div>
                <div>
                  <Label>{t('od.customerPhone')}</Label>
                  <Input
                    value={chatForm.customer_phone}
                    onChange={(e) => setChatForm({ ...chatForm, customer_phone: e.target.value })}
                    disabled={chatActive}
                  />
                </div>
              </div>
              <div>
                <Label>{t('od.testEmailOverride')} <span className="text-xs text-gray-400 font-normal">{t('od.optionalSimulatorOnly')}</span></Label>
                <Input
                  type="email"
                  placeholder={t('od.emailOverridePlaceholder')}
                  value={chatForm.test_email_override}
                  onChange={(e) => setChatForm({ ...chatForm, test_email_override: e.target.value })}
                  disabled={chatActive}
                />
              </div>
              <div className="flex gap-2">
                {!chatActive ? (
                  <Button onClick={handleInitiateChat} disabled={loading} className="flex-1">
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageSquare className="mr-2 h-4 w-4" />}
                    {t('od.startChat')}
                  </Button>
                ) : (
                  <Button onClick={handleEndChat} variant="destructive" className="flex-1">
                    <X className="mr-2 h-4 w-4" />
                    {t('od.endChat')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {selectedScenario && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">{t('od.selectedScenario')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge>{selectedScenario.language}</Badge>
                  <Badge variant="outline">{selectedScenario.complexity}</Badge>
                  {selectedScenario.emotion && (
                    <Badge className="bg-orange-500">{selectedScenario.emotion}</Badge>
                  )}
                </div>
                <p className="text-xs text-gray-600">
                  <strong>{t('od.expected')}</strong> {selectedScenario.expectedOutcome}
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>{t('od.conversation')}</CardTitle>
              {currentSession && (
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline">{t('od.sessionLabel')} {currentSession.session_id?.substring(0, 8)}</Badge>
                  <Badge variant="outline">{t('od.languageLabel')} {currentSession.detected_language || t('od.detecting')}</Badge>
                  {currentSession.detected_intent && (
                    <Badge variant="outline">{t('od.intentLabel')} {currentSession.detected_intent}</Badge>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="flex flex-col p-4" style={{ height: '480px' }}>
              {!chatActive ? (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                  <MessageSquare className="w-12 h-12 mb-4 text-gray-400" />
                  <p>{t('od.startChatToTest')}</p>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto space-y-3 min-h-0 mb-3">
                    {conversationHistory.map((turn, idx) => (
                      <div
                        key={idx}
                        className={`flex gap-2 ${turn.speaker === 'customer' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] p-3 rounded-lg ${
                            turn.speaker === 'customer' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {turn.speaker === 'customer' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                            <span className="text-xs font-semibold">
                              {turn.speaker === 'customer' ? t('od.customer') : t('od.aiAgent')}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{turn.text}</p>
                          {turn.metadata && (
                            <div className="mt-2 flex gap-1 flex-wrap">
                              {turn.metadata.detected_language && (
                                <Badge variant="secondary" className="text-xs">{turn.metadata.detected_language}</Badge>
                              )}
                              {turn.metadata.confidence && (
                                <Badge variant="secondary" className="text-xs">{(turn.metadata.confidence * 100).toFixed(0)}%</Badge>
                              )}
                              {turn.metadata.should_escalate && (
                                <Badge className="bg-red-500 text-xs">{t('od.escalated')}</Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="flex-shrink-0 space-y-2 border-t pt-3">
                    <Textarea
                      placeholder={t('od.chatInputPlaceholder')}
                      value={customerMessage}
                      onChange={(e) => setCustomerMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (customerMessage.trim() && !loading) {
                            handleSendMessage();
                          }
                        }
                      }}
                      rows={3}
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSendMessage()}
                        disabled={loading || !customerMessage.trim()}
                        className="flex-1"
                      >
                        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                        {t('od.sendMessage')}
                      </Button>
                      {customerMessage && (
                        <Button variant="outline" onClick={() => setCustomerMessage('')}>
                          {t('od.clear')}
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIChatSimulatorEnhanced;